import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import getAppSite from 'utils/getAppSite';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

const LONDON = 'Europe/London';
const EST = 'America/New_York';
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const CALENDAR_MIDNIGHT_PATTERN = /^\d{4}-\d{2}-\d{2}T00:00:00$/;

const normalMonths = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const shortMonthsLowerCase = [
  'jan',
  'feb',
  'march',
  'april',
  'may',
  'june',
  'july',
  'aug',
  'sept',
  'oct',
  'nov',
  'dec',
];

const shortMonths = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

/**
 * Class for site-time conversions. Use time() to get instances.
 * Returns dayjs-compatible objects with additional methods.
 */
class Time {
  static getSiteTimezone() {
    return getAppSite() === 'usa' ? EST : LONDON;
  }

  static isCalendarDateString(date) {
    return (
      typeof date === 'string' && (DATE_ONLY_PATTERN.test(date) || CALENDAR_MIDNIGHT_PATTERN.test(date))
    );
  }

  static parseCalendarDateString(date) {
    return dayjs.tz(date.slice(0, 10), Time.getSiteTimezone());
  }

  static parseTime(date, { utc = false, skipTimezone = false } = {}) {
    const tz = this.getSiteTimezone();
    if (date === undefined || date === null) return dayjs().tz(tz);
    if (typeof date === 'string') {
      if (utc) {
        if (skipTimezone) return dayjs.utc(date);
        return dayjs.utc(date).tz(tz);
      }
      if (Time.isCalendarDateString(date)) return Time.parseCalendarDateString(date);
      if (tz === EST) return dayjs.tz(date, LONDON).utc().tz(EST);
      return dayjs.tz(date, LONDON);
    }
    return dayjs(date).tz(tz);
  }

  static utc(date) {
    return dayjs.utc(date);
  }

  static ukTimeToUTC(isoString) {
    return dayjs.tz(isoString, LONDON).utc();
  }

  static formatDate(number, item) {
    return dayjs().add(number, item).subtract(1, 'day').utc().startOf('day').format().replace('Z', '');
  }

  static getUKDayTime() {
    const hours = new Date().toLocaleString('en-GB', {
      hour: '2-digit',
      hour12: false,
      timeZone: LONDON,
    });
    return hours >= 18 || hours < 6 ? 'dark' : 'day';
  }

  static getCalendarDateKey(date) {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      return date.slice(0, 10);
    }
    if (date === undefined || date === null) {
      return Time.create().formatCalendarDate();
    }
    return Time.create(date).formatCalendarDate();
  }

  static normalizeDateLocale() {
    dayjs.updateLocale('en', {
      monthsShort: shortMonths,
      months: normalMonths,
      weekStart: 1,
    });
  }

  static create(date, { utc = false, skipTimezone = false } = {}) {
    const d = Time.#parse(date, { utc, skipTimezone });
    Object.assign(d, {
      toUserTimezone() {
        return this.tz(dayjs.tz.guess());
      },
      formatInUserTz(formatStr) {
        return this.tz(dayjs.tz.guess()).format(formatStr);
      },
      getSiteTimezone: () => Time.getSiteTimezone(),
      formatGeneralDate(includeHour, notIncludeDay, noComma) {
        if (includeHour)
          return this.format(getAppSite() === 'usa' ? 'MMM D[,] YYYY h:mm A [ET]' : 'DD MMM[,] YYYY HH:mm');
        if (notIncludeDay) return this.format('MMM YYYY');
        if (noComma) return this.format(getAppSite() === 'usa' ? 'MMM D YYYY' : 'DD MMM YYYY');
        return this.format(getAppSite() === 'usa' ? 'MMM D[,] YYYY' : 'DD MMM[,] YYYY');
      },
      formatSimpleGeneralDate(fullMonth = false) {
        return this.format(getAppSite() === 'usa' ? 'MMM D[,] YYYY' : fullMonth ? 'D MMMM[,] YYYY' : 'D MMM[,] YYYY');
      },
      formatSimpleDay() {
        return this.format('DD MMM');
      },
      formatDayFullMonth() {
        return this.format('DD MMMM');
      },
      formatWeekdayShortDate() {
        return this.format('dddd[,] DD MMM YYYY');
      },
      formatWeekdayLongDate(noYear) {
        return this.format(
          getAppSite() === 'usa' ? `dddd MMMM D${noYear ? '' : ', YYYY'}` : `dddd D MMMM${noYear ? '' : ' YYYY'}`
        );
      },
      formatShortDate() {
        return this.format(getAppSite() === 'usa' ? 'MMM D YYYY' : 'D MMM YYYY');
      },
      formatShortDateWithTime() {
        return this.format(getAppSite() === 'usa' ? 'MMM D YYYY [at] h:mm A [ET]' : 'D MMM YYYY [at] HH:mm');
      },
      formatSlashDate() {
        return this.format(getAppSite() === 'usa' ? 'MM/DD/YY' : 'DD/MM/YY');
      },
      formatLongDate(comma = true) {
        return this.format(getAppSite() === 'usa' ? 'MMMM D, YYYY' : comma ? `DD MMMM, YYYY` : `DD MMMM YYYY`);
      },
      formatLongDateWithTime() {
        return this.format(getAppSite() === 'usa' ? 'MMMM D, YYYY [at] h:mm A [ET]' : 'DD MMMM YYYY [at] hh:mm a');
      },
      formatTime() {
        return this.format(getAppSite() === 'usa' ? 'h:mm A [ET]' : 'HH:mm');
      },
      formatCalendarDate() {
        return this.format('YYYY-MM-DD');
      },
      formatEmbargoedDate() {
        return this.format(getAppSite() === 'usa' ? 'MMM D[,] YYYY [at] h:mm A [ET]' : 'DD MMM[,] YYYY [at] HH:mm');
      },
      formatEmbargoedDateNoYear() {
        return this.format(getAppSite() === 'usa' ? 'MMM D[,] [at] h:mm A [ET]' : 'DD MMM[,] [at] HH:mm');
      },
      trendsFormat(month) {
        return month ? this.format('MMM YYYY') : this.format(getAppSite() === 'usa' ? 'MMM D[,] YY' : 'DD MMM[,] YY');
      },
      formatMonthFirstChar() {
        return this.format('MMM')[0];
      },
      formatShortMonth() {
        return this.format('MMM');
      },
      startOfMonth() {
        return this.startOf('month');
      },
      formatRelativeDay() {
        const today = time();
        const yesterday = today.subtract(1, 'day');
        if (this.isSame(today, 'day')) return 'Today';
        if (this.isSame(yesterday, 'day')) return 'Yesterday';
        return this.format('dddd, MMM Do');
      },
    });
    return d;
  }

  static #parse(date, { utc = false, skipTimezone = false } = {}) {
    const tz = Time.getSiteTimezone();
    if (date === undefined || date === null) return dayjs().tz(tz);
    if (typeof date !== 'string') return dayjs(date).tz(tz);
    if (utc) {
      if (skipTimezone) return dayjs.utc(date);
      else return dayjs.utc(date).tz(tz);
    }
    if (Time.isCalendarDateString(date)) return Time.parseCalendarDateString(date);
    if (tz === EST) return Time.ukTimeToUTC(date).tz(EST);
    return dayjs.tz(date, LONDON);
  }
}

// Static properties (arrays)
Time.normalMonths = normalMonths;
Time.shortMonths = shortMonths;
Time.shortMonthsLowerCase = shortMonthsLowerCase;

// Set default timezone for dayjs.tz() calls (runs when module loads)
dayjs.tz.setDefault(Time.getSiteTimezone());

// Initialize dayjs locale with custom month names on module load
Time.normalizeDateLocale();

/**
 * Backend UK or UTC string → dayjs in site timezone.
 * Pass {utc: true} for UTC-sourced strings (e.g. createdAtUtc).
 * Pass {utc: true, skipTimezone: true} for UTC strings that should not be converted to site timezone (e.g. dates of US legislation stages).
 * Returns a dayjs object with extra SiteTime methods.
 *
 * Usage:
 * - time(date)
 * - time(date, {utc: true})
 * - time(date, {utc: true, skipTimezone: true})
 */
const time = Object.assign((date, { utc = false, skipTimezone = false } = {}) => {
  return Time.create(date, { utc, skipTimezone });
}, Time);

// Copy static methods onto export (class statics are non-enumerable so Object.assign doesn't copy them)
time.normalizeDateLocale = Time.normalizeDateLocale;
time.formatDate = Time.formatDate;
time.getSiteTimezone = Time.getSiteTimezone;
time.parseTime = Time.parseTime;
time.utc = Time.utc;
time.ukTimeToUTC = Time.ukTimeToUTC;
time.getUKDayTime = Time.getUKDayTime;
time.getCalendarDateKey = Time.getCalendarDateKey;
time.isCalendarDateString = Time.isCalendarDateString;
time.parseCalendarDateString = Time.parseCalendarDateString;

export default time;
